import { DRUM_STEPS_PER_MEASURE } from '../types';
import type { ChordAccompaniment, ChordId } from '../types';
import { CELESTIAL_CHORDS, CHORD_IDS } from '../audio/celestialPadEngine';

export function createEmptyMeasureChords(): Record<ChordId, boolean[]> {
  const row = () => Array<boolean>(DRUM_STEPS_PER_MEASURE).fill(false);
  return Object.fromEntries(CHORD_IDS.map((id) => [id, row()])) as Record<ChordId, boolean[]>;
}

export function createEmptyChordAccompaniment(measureCount: number): ChordAccompaniment {
  return {
    enabled: true,
    measures: Array.from({ length: measureCount }, () => ({
      steps: createEmptyMeasureChords(),
    })),
    voicings: {},
  };
}

export function resizeChordAccompaniment(
  prev: ChordAccompaniment,
  measureCount: number,
): ChordAccompaniment {
  const measures = Array.from({ length: measureCount }, (_, i) => {
    const existing = prev.measures[i];
    if (existing) return { steps: cloneSteps(existing.steps) };
    return { steps: createEmptyMeasureChords() };
  });
  return { ...prev, measures, voicings: prev.voicings ?? {} };
}

function cloneSteps(steps: Record<ChordId, boolean[]>): Record<ChordId, boolean[]> {
  return Object.fromEntries(
    CHORD_IDS.map((id) => [id, [...(steps[id] ?? [])]]),
  ) as Record<ChordId, boolean[]>;
}

export function toggleChordStep(
  acc: ChordAccompaniment,
  measureIndex: number,
  chordId: ChordId,
  stepIndex: number,
): ChordAccompaniment {
  const measure = acc.measures[measureIndex];
  if (!measure) return acc;
  const row = [...measure.steps[chordId]];
  if (stepIndex < 0 || stepIndex >= row.length) return acc;
  row[stepIndex] = !row[stepIndex];
  const measures = acc.measures.map((m, i) =>
    i !== measureIndex ? m : { steps: { ...m.steps, [chordId]: row } },
  );
  return { ...acc, enabled: true, measures };
}

export function extendChordRange(
  acc: ChordAccompaniment,
  measureIndex: number,
  chordId: ChordId,
  fromStep: number,
  toStep: number,
): ChordAccompaniment {
  const measure = acc.measures[measureIndex];
  if (!measure) return acc;
  const lo = Math.min(fromStep, toStep);
  const hi = Math.max(fromStep, toStep);
  const row = [...measure.steps[chordId]];
  for (let s = lo; s <= hi && s < row.length; s++) {
    row[s] = true;
  }
  const measures = acc.measures.map((m, i) =>
    i !== measureIndex ? m : { steps: { ...m.steps, [chordId]: row } },
  );
  return { ...acc, enabled: true, measures };
}

export interface ChordRun {
  chordId: ChordId;
  startStep: number;
  endStep: number;
}

export function chordRunsInMeasure(acc: ChordAccompaniment, measureIndex: number): ChordRun[] {
  const measure = acc.measures[measureIndex];
  if (!measure) return [];
  const runs: ChordRun[] = [];
  for (const chordId of CHORD_IDS) {
    const row = measure.steps[chordId];
    let step = 0;
    while (step < row.length) {
      if (!row[step]) {
        step += 1;
        continue;
      }
      const startStep = step;
      while (step < row.length && row[step]) step += 1;
      runs.push({ chordId, startStep, endStep: step - 1 });
    }
  }
  return runs.sort((a, b) => a.startStep - b.startStep);
}

export function isChordRunStart(row: boolean[], step: number): boolean {
  return Boolean(row[step] && (step === 0 || !row[step - 1]));
}

export function isChordRunHold(row: boolean[], step: number): boolean {
  return Boolean(row[step] && step > 0 && row[step - 1]);
}

export function clearChordRow(
  acc: ChordAccompaniment,
  measureIndex: number,
  chordId: ChordId,
): ChordAccompaniment {
  const measure = acc.measures[measureIndex];
  if (!measure) return acc;
  const measures = acc.measures.map((m, i) =>
    i !== measureIndex
      ? m
      : { steps: { ...m.steps, [chordId]: m.steps[chordId].map(() => false) } },
  );
  return { ...acc, measures };
}

export function chordsAtStep(
  acc: ChordAccompaniment,
  measureIndex: number,
  stepIndex: number,
): ChordId[] {
  const measure = acc.measures[measureIndex];
  if (!measure) return [];
  return CHORD_IDS.filter((id) => measure.steps[id][stepIndex]);
}

export function measureHasChords(acc: ChordAccompaniment, measureIndex: number): boolean {
  const measure = acc.measures[measureIndex];
  if (!measure) return false;
  return CHORD_IDS.some((id) => measure.steps[id].some(Boolean));
}

export function accompanimentHasChords(acc: ChordAccompaniment): boolean {
  return acc.measures.some((_, i) => measureHasChords(acc, i));
}

export interface MeasureChordSummary {
  hasChords: boolean;
  primaryChordId: ChordId | null;
  /** Nombre simbólico del acorde (p. ej. G, Am) o «Armonía» si está vacío. */
  displayName: string;
}

export function measureChordSummaries(acc: ChordAccompaniment): MeasureChordSummary[] {
  return acc.measures.map((_, i) => measureChordSummary(acc, i));
}

export function measureChordSummary(
  acc: ChordAccompaniment,
  measureIndex: number,
): MeasureChordSummary {
  const runs = chordRunsInMeasure(acc, measureIndex);
  if (runs.length === 0) {
    return { hasChords: false, primaryChordId: null, displayName: 'Armonía' };
  }

  const uniqueIds: ChordId[] = [];
  for (const run of runs) {
    if (!uniqueIds.includes(run.chordId)) uniqueIds.push(run.chordId);
  }

  const names = uniqueIds.map((id) => {
    const chord = CELESTIAL_CHORDS.find((c) => c.id === id);
    return chord?.chordName ?? id;
  });

  const displayName =
    names.length <= 2 ? names.join(' · ') : `${names[0]} +${names.length - 1}`;

  return {
    hasChords: true,
    primaryChordId: uniqueIds[0] ?? null,
    displayName,
  };
}
